/**
 * Generated by scripts/generate-drizzle-schema.mjs. Do not edit by hand.
 * This product's inputs: scripts/drizzle-schema.config.mjs.
 * Regenerate: bash scripts/client-schema-fresh.sh (data-model-change skill).
 */

import { DrizzleAppSchema, type PowerSyncSQLiteDatabase } from "@powersync/drizzle-driver";
import { index, integer, sqliteTable, text } from "drizzle-orm/sqlite-core";
import type { InferInsertModel, InferSelectModel } from "drizzle-orm";

export const bible_bookmark = sqliteTable(
  "bible_bookmark",
  {
    id: text("id").primaryKey(),
    user_id: text("user_id"),
    version: text("version"),
    book: integer("book"),
    chapter: integer("chapter"),
    verse: integer("verse"),
    created_at: text("created_at"),
    updated_at: text("updated_at"),
    deleted_at: text("deleted_at"),
  },
  (table) => [
    /** Filter user bookmarks */
    index("bible_bookmark_by_user").on(table.user_id),
    /** Fast bookmark lookup by coordinates */
    index("bible_bookmark_by_coordinates").on(
      table.version,
      table.book,
      table.chapter,
      table.verse,
    ),
  ],
);

// ⚡ User's own highlights.
export const bible_highlight = sqliteTable(
  "bible_highlight",
  {
    id: text("id").primaryKey(),
    user_id: text("user_id"),
    color: text("color"),
    created_at: text("created_at"),
    updated_at: text("updated_at"),
    deleted_at: text("deleted_at"),
    origin_id: text("origin_id"),
  },
  (table) => [
    /** Filter user highlights */
    index("bible_highlight_by_user").on(table.user_id),
  ],
);

// Tag mapping for each highlight.
export const bible_highlight_tag = sqliteTable(
  "bible_highlight_tag",
  {
    id: text("id").primaryKey(),
    highlight_id: text("highlight_id"),
    tag: text("tag"),
  },
  (table) => [
    /** Tag lookup by highlight ID */
    index("bible_highlight_tag_by_highlight").on(table.highlight_id),
    /** Highlight lookup by tag */
    index("bible_highlight_tag_by_tag").on(table.tag),
  ],
);

// Verse mapping for each highlight.
export const bible_highlight_verse = sqliteTable(
  "bible_highlight_verse",
  {
    id: text("id").primaryKey(),
    highlight_id: text("highlight_id"),
    version: text("version"),
    book: integer("book"),
    chapter: integer("chapter"),
    verse: integer("verse"),
  },
  (table) => [
    /** Highlight verse lookup by highlight ID */
    index("bible_highlight_verse_by_highlight").on(table.highlight_id),
    /** Fast highlight verse lookup and rendering by coordinates */
    index("bible_highlight_verse_by_coordinates").on(
      table.version,
      table.book,
      table.chapter,
      table.verse,
    ),
  ],
);

// ⚡ User's own notes.
export const bible_note = sqliteTable(
  "bible_note",
  {
    id: text("id").primaryKey(),
    user_id: text("user_id"),
    text_content: text("text_content"),
    color: text("color"),
    created_at: text("created_at"),
    updated_at: text("updated_at"),
    deleted_at: text("deleted_at"),
  },
  (table) => [
    /** Filter user notes */
    index("bible_note_by_user").on(table.user_id),
  ],
);

// Tag mapping for each note.
export const bible_note_tag = sqliteTable(
  "bible_note_tag",
  {
    id: text("id").primaryKey(),
    note_id: text("note_id"),
    tag: text("tag"),
  },
  (table) => [
    /** Tag lookup by note ID */
    index("bible_note_tag_by_note").on(table.note_id),
    /** Note lookup by tag */
    index("bible_note_tag_by_tag").on(table.tag),
  ],
);

// Verse mapping for each note.
export const bible_note_verse = sqliteTable(
  "bible_note_verse",
  {
    id: text("id").primaryKey(),
    note_id: text("note_id"),
    version: text("version"),
    book: integer("book"),
    chapter: integer("chapter"),
    verse: integer("verse"),
  },
  (table) => [
    /** Note verse lookup by note ID */
    index("bible_note_verse_by_note").on(table.note_id),
    /** Fast note verse lookup and rendering by coordinates */
    index("bible_note_verse_by_coordinates").on(
      table.version,
      table.book,
      table.chapter,
      table.verse,
    ),
  ],
);

export const bible_reading_progress = sqliteTable(
  "bible_reading_progress",
  {
    id: text("id").primaryKey(),
    user_id: text("user_id"),
    plan_id: text("plan_id"),
    day: integer("day"),
    created_at: text("created_at"),
    updated_at: text("updated_at"),
    deleted_at: text("deleted_at"),
  },
  (table) => [
    /** Filter user reading progress */
    index("bible_reading_progress_by_user").on(table.user_id),
    /** Completed days for a specific plan */
    index("bible_reading_progress_by_plan").on(table.user_id, table.plan_id, table.day),
  ],
);

// ⚡ Bible text for OFFLINE reader (global catalog, seeded locally).
export const bible_verse_content = sqliteTable(
  "bible_verse_content",
  {
    id: text("id").primaryKey(),
    version: text("version"),
    book: integer("book"),
    chapter: integer("chapter"),
    verse: integer("verse"),
    text: text("text"),
    created_at: text("created_at"),
    updated_at: text("updated_at"),
  },
  (table) => [
    /** Offline reader rendering: verses of a chapter */
    index("bible_verse_content_by_coordinates").on(
      table.version,
      table.book,
      table.chapter,
      table.verse,
    ),
  ],
);

// ⚡ Individual highlights at verse level.
export const bible_verse_highlight = sqliteTable(
  "bible_verse_highlight",
  {
    id: text("id").primaryKey(),
    user_id: text("user_id"),
    version: text("version"),
    book: integer("book"),
    chapter: integer("chapter"),
    verse: integer("verse"),
    color: text("color"),
    origin_id: text("origin_id"),
    created_at: text("created_at"),
    updated_at: text("updated_at"),
    deleted_at: text("deleted_at"),
  },
  (table) => [
    /** Individual verse highlights by user */
    index("bible_verse_highlight_by_user").on(table.user_id),
    /** Fast verse highlight lookup by coordinates */
    index("bible_verse_highlight_by_coordinates").on(
      table.version,
      table.book,
      table.chapter,
      table.verse,
    ),
  ],
);

// Global metadata for Bible versions.
export const bible_version = sqliteTable("bible_version", {
  id: text("id").primaryKey(),
  name: text("name"),
  abbr: text("abbr"),
  language_iso2: text("language_iso2"),
  language_iso3: text("language_iso3"),
  language_name_english: text("language_name_english"),
  language_name_native: text("language_name_native"),
  status: text("status"),
  source_info_url: text("source_info_url"),
  source_info_download_url: text("source_info_download_url"),
  created_at: text("created_at"),
  updated_at: text("updated_at"),
  deleted_at: text("deleted_at"),
});

// Files attached to Bible versions.
export const bible_version_file = sqliteTable(
  "bible_version_file",
  {
    id: text("id").primaryKey(),
    bible_version_id: text("bible_version_id"),
    file_type: text("file_type"),
    original_name: text("original_name"),
    extension: text("extension"),
    relative_path: text("relative_path"),
    sha256: text("sha256"),
    created_at: text("created_at"),
    updated_at: text("updated_at"),
  },
  (table) => [
    /** Bible version file lookup */
    index("bible_version_file_by_bible_version").on(table.bible_version_id),
  ],
);

export const font_family = sqliteTable("font_family", {
  id: text("id").primaryKey(),
  family: text("family"),
  name: text("name"),
  category: text("category"),
  is_active: integer("is_active"),
  sort_order: integer("sort_order"),
  created_at: text("created_at"),
  updated_at: text("updated_at"),
});

export const font_variant = sqliteTable("font_variant", {
  id: text("id").primaryKey(),
  font_family_id: text("font_family_id"),
  weight: text("weight"),
  url: text("url"),
  created_at: text("created_at"),
  attachment_id: text("attachment_id"),
});

// Small dataset per user — indexes not needed for now.
export const member = sqliteTable("member", {
  id: text("id").primaryKey(),
  organization_id: text("organization_id"),
  user_id: text("user_id"),
  role: text("role"),
  created_at: text("created_at"),
});

// Small dataset — indexes not needed for now.
export const organization = sqliteTable("organization", {
  id: text("id").primaryKey(),
  name: text("name"),
  slug: text("slug"),
  logo: text("logo"),
  metadata: text("metadata"),
  created_at: text("created_at"),
});

// Active user sessions — small dataset, no indexes.
export const session = sqliteTable("session", {
  id: text("id").primaryKey(),
});

// ⚡ Strong lexicon for offline concordance (global catalog, seeded locally).
export const strong_entry = sqliteTable(
  "strong_entry",
  {
    id: text("id").primaryKey(),
    code: text("code"),
    lemma: text("lemma"),
    transliteration: text("transliteration"),
    pronunciation: text("pronunciation"),
    gloss: text("gloss"),
    definition: text("definition"),
    language: text("language"),
    created_at: text("created_at"),
    updated_at: text("updated_at"),
  },
  (table) => [
    /** Search Strong entry by canonical code */
    index("strong_entry_by_code").on(table.code),
  ],
);

// Small global dataset (~20 themes) — indexes not needed.
export const themes = sqliteTable("themes", {
  id: text("id").primaryKey(),
  name: text("name"),
  background: text("background"),
  surface: text("surface"),
  accent: text("accent"),
  glow_colors: text("glow_colors"),
  created_at: text("created_at"),
  updated_at: text("updated_at"),
  is_dark: integer("is_dark"),
  foreground: text("foreground"),
  field_background: text("field_background"),
  danger: text("danger"),
  success: text("success"),
  warning: text("warning"),
  muted: text("muted"),
  pattern_type: text("pattern_type"),
  pattern_opacity: integer("pattern_opacity"),
  pair_id: text("pair_id"),
  font_normal: text("font_normal"),
  font_medium: text("font_medium"),
  font_semibold: text("font_semibold"),
  font_bold: text("font_bold"),
  radius: text("radius"),
  surface_foreground: text("surface_foreground"),
  surface_secondary: text("surface_secondary"),
  surface_secondary_foreground: text("surface_secondary_foreground"),
  surface_tertiary: text("surface_tertiary"),
  surface_tertiary_foreground: text("surface_tertiary_foreground"),
  overlay: text("overlay"),
  overlay_foreground: text("overlay_foreground"),
  default: text("default"),
  default_foreground: text("default_foreground"),
  accent_foreground: text("accent_foreground"),
  field_foreground: text("field_foreground"),
  field_placeholder: text("field_placeholder"),
  field_border: text("field_border"),
  danger_foreground: text("danger_foreground"),
  success_foreground: text("success_foreground"),
  warning_foreground: text("warning_foreground"),
  segment: text("segment"),
  segment_foreground: text("segment_foreground"),
  border: text("border"),
  focus: text("focus"),
  link: text("link"),
  surface_shadow: text("surface_shadow"),
  overlay_shadow: text("overlay_shadow"),
  field_shadow: text("field_shadow"),
  muted_foreground: text("muted_foreground"),
  divider: text("divider"),
  glass_background: text("glass_background"),
  glass_border: text("glass_border"),
  glass_highlight: text("glass_highlight"),
  is_active: integer("is_active"),
  sort_order: integer("sort_order"),
});

// Only 1 row per user — indexes not needed.
export const user = sqliteTable("user", {
  id: text("id").primaryKey(),
  name: text("name"),
  email: text("email"),
  email_verified: integer("email_verified"),
  image: text("image"),
  created_at: text("created_at"),
  updated_at: text("updated_at"),
  locale: text("locale"),
  global_theme_id: text("global_theme_id"),
  is_blur: integer("is_blur"),
  font_family: text("font_family"),
  follow_device_theme: integer("follow_device_theme"),
  role: text("role"),
});

export const drizzleSchema = {
  bible_bookmark,
  bible_highlight,
  bible_highlight_tag,
  bible_highlight_verse,
  bible_note,
  bible_note_tag,
  bible_note_verse,
  bible_reading_progress,
  bible_verse_content,
  bible_verse_highlight,
  bible_version,
  bible_version_file,
  font_family,
  font_variant,
  member,
  organization,
  session,
  strong_entry,
  themes,
  user,
};

export const AppSchema = new DrizzleAppSchema(drizzleSchema);

export type Database = (typeof AppSchema)["types"];

/**
 * Type helper for the Drizzle database instance wrapped with PowerSync.
 */
export type PowerSyncDrizzleDB = PowerSyncSQLiteDatabase<typeof drizzleSchema>;

export const USER_DATA_TABLES = [
  "bible_bookmark",
  "bible_highlight",
  "bible_note",
  "bible_reading_progress",
  "bible_verse_highlight",
] as const;

export type BibleBookmark = InferSelectModel<typeof bible_bookmark>;
export type BibleBookmarkInsert = InferInsertModel<typeof bible_bookmark>;
export type BibleBookmarkRecord = BibleBookmark;
export type BibleHighlight = InferSelectModel<typeof bible_highlight>;
export type BibleHighlightInsert = InferInsertModel<typeof bible_highlight>;
export type BibleHighlightRecord = BibleHighlight;
export type BibleHighlightTag = InferSelectModel<typeof bible_highlight_tag>;
export type BibleHighlightTagInsert = InferInsertModel<typeof bible_highlight_tag>;
export type BibleHighlightTagRecord = BibleHighlightTag;
export type BibleHighlightVerse = InferSelectModel<typeof bible_highlight_verse>;
export type BibleHighlightVerseInsert = InferInsertModel<typeof bible_highlight_verse>;
export type BibleHighlightVerseRecord = BibleHighlightVerse;
export type BibleNote = InferSelectModel<typeof bible_note>;
export type BibleNoteInsert = InferInsertModel<typeof bible_note>;
export type BibleNoteRecord = BibleNote;
export type BibleNoteTag = InferSelectModel<typeof bible_note_tag>;
export type BibleNoteTagInsert = InferInsertModel<typeof bible_note_tag>;
export type BibleNoteTagRecord = BibleNoteTag;
export type BibleNoteVerse = InferSelectModel<typeof bible_note_verse>;
export type BibleNoteVerseInsert = InferInsertModel<typeof bible_note_verse>;
export type BibleNoteVerseRecord = BibleNoteVerse;
export type BibleReadingProgress = InferSelectModel<typeof bible_reading_progress>;
export type BibleReadingProgressInsert = InferInsertModel<typeof bible_reading_progress>;
export type BibleReadingProgressRecord = BibleReadingProgress;
export type BibleVerseContent = InferSelectModel<typeof bible_verse_content>;
export type BibleVerseContentInsert = InferInsertModel<typeof bible_verse_content>;
export type BibleVerseContentRecord = BibleVerseContent;
export type BibleVerseHighlight = InferSelectModel<typeof bible_verse_highlight>;
export type BibleVerseHighlightInsert = InferInsertModel<typeof bible_verse_highlight>;
export type BibleVerseHighlightRecord = BibleVerseHighlight;
export type BibleVersion = InferSelectModel<typeof bible_version>;
export type BibleVersionInsert = InferInsertModel<typeof bible_version>;
export type BibleVersionRecord = BibleVersion;
export type BibleVersionFile = InferSelectModel<typeof bible_version_file>;
export type BibleVersionFileInsert = InferInsertModel<typeof bible_version_file>;
export type BibleVersionFileRecord = BibleVersionFile;
export type FontFamily = InferSelectModel<typeof font_family>;
export type FontFamilyInsert = InferInsertModel<typeof font_family>;
export type FontFamilyRecord = FontFamily;
export type FontVariant = InferSelectModel<typeof font_variant>;
export type FontVariantInsert = InferInsertModel<typeof font_variant>;
export type FontVariantRecord = FontVariant;
export type Member = InferSelectModel<typeof member>;
export type MemberInsert = InferInsertModel<typeof member>;
export type MemberRecord = Member;
export type Organization = InferSelectModel<typeof organization>;
export type OrganizationInsert = InferInsertModel<typeof organization>;
export type OrganizationRecord = Organization;
export type Session = InferSelectModel<typeof session>;
export type SessionInsert = InferInsertModel<typeof session>;
export type SessionRecord = Session;
export type StrongEntry = InferSelectModel<typeof strong_entry>;
export type StrongEntryInsert = InferInsertModel<typeof strong_entry>;
export type StrongEntryRecord = StrongEntry;
export type Themes = InferSelectModel<typeof themes>;
export type ThemesInsert = InferInsertModel<typeof themes>;
export type ThemesRecord = Themes;
export type User = InferSelectModel<typeof user>;
export type UserInsert = InferInsertModel<typeof user>;
export type UserRecord = User;
